import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const TopUnshippedWidget = ({ orders }) => {
    const { userData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [topProducts, setTopProducts] = useState([]);

    useEffect(() => {
        if (!userData?.companyId || !orders) return;

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                const unshippedMap = new Map();

                // To avoid overloading, maybe we only check pending and partial orders
                const activeOrders = orders.filter(o => o.status === 'Sipariş Alındı' || o.status === 'Kısmen Gönderildi');

                for (const order of activeOrders) {
                    const shipmentsSnapshot = await new Promise((resolve) => {
                        const unsubscribe = onSnapshot(
                            collection(db, 'companies', userData.companyId, 'orders', order.id, 'shipments'),
                            (snapshot) => {
                                resolve(snapshot);
                                unsubscribe();
                            }
                        );
                    });

                    const shipments = shipmentsSnapshot.docs.map(doc => doc.data());
                    const shippedQuantities = {};
                    
                    shipments.forEach(shipment => {
                        (shipment.items || []).forEach(item => {
                            shippedQuantities[item.productId] = (shippedQuantities[item.productId] || 0) + item.quantity;
                        });
                    });

                    (order.lineItems || []).forEach(item => {
                        const shipped = shippedQuantities[item.productId] || 0;
                        const unshipped = item.quantity - shipped;

                        if (unshipped > 0) {
                            if (unshippedMap.has(item.productId)) {
                                const current = unshippedMap.get(item.productId);
                                current.unshipped += unshipped;
                                current.count += 1;
                            } else {
                                unshippedMap.set(item.productId, {
                                    productId: item.productId,
                                    name: item.productName || 'Bilinmeyen Ürün',
                                    unshipped: unshipped,
                                    count: 1
                                });
                            }
                        }
                    });
                }

                const sData = Array.from(unshippedMap.values());
                sData.sort((a, b) => b.unshipped - a.unshipped);
                
                // Get top 5
                const top5 = sData.slice(0, 5);

                // Fetch images for top 5 only
                if (top5.length > 0) {
                    const productsSnapshot = await new Promise((resolve) => {
                        const unsubscribe = onSnapshot(
                            collection(db, 'companies', userData.companyId, 'products'),
                            (snapshot) => {
                                resolve(snapshot);
                                unsubscribe();
                            }
                        );
                    });
                    
                    const productsDocs = productsSnapshot.docs;
                    top5.forEach(item => {
                        const pDoc = productsDocs.find(d => d.id === item.productId);
                        if (pDoc) {
                            item.imageUrl = pDoc.data().imageUrl || null;
                            if (pDoc.data().name) item.name = pDoc.data().name;
                        }
                    });
                }

                if (isMounted) {
                    setTopProducts(top5);
                    setLoading(false);
                }

            } catch (error) {
                console.error('Widget error:', error);
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [userData, orders]);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>Hesaplanıyor...</div>
            </div>
        );
    }

    if (topProducts.length === 0) {
        return (
            <div style={styles.emptyContainer}>
                <span style={{ fontSize: '24px' }}>🎉</span>
                <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '14px' }}>Tüm siparişler gönderilmiş!</p>
            </div>
        );
    }

    return (
        <div style={styles.list}>
            {topProducts.map((p, idx) => (
                <div key={p.productId} style={styles.listItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <div style={{ width: '28px', height: '28px', background: '#fff7ed', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#ea580c', fontSize: '12px', flexShrink: 0 }}>
                            {idx + 1}
                        </div>
                        {p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />
                        ) : (
                            <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>📦</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <span style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>{p.count} Farklı Siparişte</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                        <span style={{ fontWeight: '700', color: '#ef4444', fontSize: '15px' }}>{p.unshipped} <span style={{fontSize:'11px', fontWeight:'500'}}>Bekleyen</span></span>
                    </div>
                </div>
            ))}
        </div>
    );
};

const styles = {
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px'
    },
    spinner: {
        width: '24px',
        height: '24px',
        border: '2px solid #e2e8f0',
        borderTopColor: '#f59e0b',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px',
        textAlign: 'center'
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '16px'
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 12px',
        background: '#f8fafc',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        border: '1px solid transparent',
        ':hover': {
            background: '#f1f5f9',
            transform: 'translateX(4px)'
        }
    }
};

export default TopUnshippedWidget;
